// Home-scope data hook extracted from Sidebar.tsx during the P5 split.
// Owns the home-only memo chain (channels filter -> workspace unreads ->
// per-workspace activity -> best lookup -> filtered workspace list) plus
// the four UI-state values the home content reads: section open flags,
// section filters, and `showCreateWorkspaceHome`.
//
// The parent passes in the data slices (channelMap, mutedSet, count maps,
// workspaceList). Returning one object keeps the parent destructuring tight.

import { useMemo, useState } from "react";
import {
  aggregateWorkspaceUnreads,
  computeWorkspaceLastActivity,
  pickBestLookupWorkspace,
  type WorkspaceUnreadStats,
} from "./sidebar-home-aggregations";

type HomeFilter = "all" | "recent";

interface ChannelLike {
  id: string;
  workspace_id?: string | null;
  is_archived?: boolean | null;
}

interface UseSidebarHomeDataInput {
  isHomeScope: boolean;
  channelMap: Map<string, ChannelLike>;
  mutedSet: Set<string>;
  unreadCountMap: Map<string, number>;
  mentionCountMap: Map<string, number>;
  lastMessageAtMap: Map<string, number>;
  // Caller's workspaceList may include nullable created_at via the loose
  // any[] shape SidebarProps uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspaceList: any[];
}

export interface SidebarHomeData {
  homeWsOpen: boolean;
  setHomeWsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  homeDmsOpen: boolean;
  setHomeDmsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  homeWsFilter: HomeFilter;
  setHomeWsFilter: React.Dispatch<React.SetStateAction<HomeFilter>>;
  homeDmsFilter: HomeFilter;
  setHomeDmsFilter: React.Dispatch<React.SetStateAction<HomeFilter>>;
  showCreateWorkspaceHome: boolean;
  setShowCreateWorkspaceHome: React.Dispatch<React.SetStateAction<boolean>>;
  workspaceUnreads: Map<string, WorkspaceUnreadStats>;
  workspaceLastActivity: Map<string, number>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  homeDisplayWorkspaces: any[];
  bestLookupWorkspaceId: string | undefined;
}

export function useSidebarHomeData({
  isHomeScope,
  channelMap,
  mutedSet,
  unreadCountMap,
  mentionCountMap,
  lastMessageAtMap,
  workspaceList,
}: UseSidebarHomeDataInput): SidebarHomeData {
  // D.1: replaces `useQuery(queries.channels.activeByUser())`. The composite
  // returns ALL my memberships' channels; we filter to non-archived for the
  // workspace aggregations (matches activeByUser's semantics). Always-on
  // because workspaceUnreads is consumed by both home tiles and the
  // workspace switcher dropdown (reachable from every scope).
  const allUserChannels = useMemo(() => {
    const out: ChannelLike[] = [];
    for (const c of channelMap.values()) {
      if (!c.is_archived) out.push(c);
    }
    return out;
  }, [channelMap]);

  // Aggregate unread per workspace. Always computed (cheap O(channels))
  // because two surfaces consume it: home-scope workspace tiles + the
  // workspace switcher dropdown's per-workspace unread dots — and the
  // switcher is reachable from every scope, not just home.
  const workspaceUnreads = useMemo(() => {
    return aggregateWorkspaceUnreads(
      allUserChannels,
      mutedSet,
      unreadCountMap,
      mentionCountMap,
    );
  }, [allUserChannels, unreadCountMap, mentionCountMap, mutedSet]);

  // Last activity per workspace (for home scope "recent" sorting)
  const workspaceLastActivity = useMemo(() => {
    if (!isHomeScope) return new Map<string, number>();
    return computeWorkspaceLastActivity(
      allUserChannels,
      lastMessageAtMap,
      workspaceList,
    );
  }, [isHomeScope, allUserChannels, lastMessageAtMap, workspaceList]);

  // Home scope section state
  const [homeWsOpen, setHomeWsOpen] = useState(true);
  const [homeDmsOpen, setHomeDmsOpen] = useState(true);
  // "all" is the safer default — "recent" renders an empty list for new
  // users without recent activity, which reads as a broken sidebar.
  const [homeWsFilter, setHomeWsFilter] = useState<HomeFilter>("all");
  const [homeDmsFilter, setHomeDmsFilter] = useState<HomeFilter>("all");
  const [showCreateWorkspaceHome, setShowCreateWorkspaceHome] = useState(false);

  const homeDisplayWorkspaces = useMemo(() => {
    if (!isHomeScope) return [];
    if (homeWsFilter === "recent") {
      // "Recent" = workspaceList sorted by last activity desc.
      // Workspaces with no channel-message activity (freshly created,
      // visited but not posted in, dormant memberships) sort to the
      // bottom but REMAIN VISIBLE. Previously the filter was
      // `workspaceLastActivity.has(ws.id)`, which excluded any
      // workspace without messages — meaning a workspace you just
      // created was invisible under the default filter until the
      // first message landed, breaking onboarding (and chaining into
      // the CLI install hook never firing because you never entered
      // the workspace).
      return [...workspaceList].sort(
        (a, b) =>
          (workspaceLastActivity.get(b.id) ?? 0) -
          (workspaceLastActivity.get(a.id) ?? 0),
      );
    }
    return workspaceList;
  }, [isHomeScope, workspaceList, homeWsFilter, workspaceLastActivity]);

  // Best workspace for email lookup in NewDM dialog (home scope)
  const bestLookupWorkspaceId = useMemo(
    () => pickBestLookupWorkspace(workspaceList, workspaceLastActivity),
    [workspaceList, workspaceLastActivity],
  );

  return {
    homeWsOpen,
    setHomeWsOpen,
    homeDmsOpen,
    setHomeDmsOpen,
    homeWsFilter,
    setHomeWsFilter,
    homeDmsFilter,
    setHomeDmsFilter,
    showCreateWorkspaceHome,
    setShowCreateWorkspaceHome,
    workspaceUnreads,
    workspaceLastActivity,
    homeDisplayWorkspaces,
    bestLookupWorkspaceId,
  };
}
